import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { Table } from '@tanstack/react-table';
import * as React from 'react';

const safeStringify = (obj: any): string => {
    try {
        return JSON.stringify(obj);
    } catch (error) {
        console.warn('Failed to stringify table state:', error);
        return '';
    }
};

/**
 * Generic hook to handle table state synchronization with parent components.
 * Eliminates the duplicated state management logic across all table components.
 */
export function useTableStateSync<TData>(
    table: Table<TData>,
    onTableStateChange: (state: MainTableState) => void,
    debounceMs = 500
) {
    // Debounced callback to notify parent of state changes
    const debouncedStateChange = useDebouncedCallback((state: MainTableState) => {
        onTableStateChange(state);
    }, debounceMs);

    // Memoize table state to avoid infinite loops
    const tableState = React.useMemo(() => {
        const { pagination, sorting, columnFilters } = table.getState();
        return {
            pagination,
            sorting,
            columnFilters,
        };
    }, [
        JSON.stringify(table.getState().pagination),
        JSON.stringify(table.getState().sorting),
        JSON.stringify(table.getState().columnFilters),
    ]);

    // Track previous state to detect changes
    const prevStateRef = React.useRef(tableState);

    // Notify parent when table state changes (debounced for filters, immediate for sorting/pagination)
    React.useEffect(() => {
        const prev = prevStateRef.current;

        // Check what changed using JSON.stringify for deep comparison
        const paginationChanged =
            safeStringify(prev.pagination) !== safeStringify(tableState.pagination);
        const sortingChanged =
            safeStringify(prev.sorting) !== safeStringify(tableState.sorting);
        const filtersChanged =
            safeStringify(prev.columnFilters) !== safeStringify(tableState.columnFilters);

        // Immediate update for pagination/sorting, debounced for filters
        if (paginationChanged || sortingChanged) {
            onTableStateChange(tableState);
        } else if (filtersChanged) {
            debouncedStateChange(tableState);
        }

        prevStateRef.current = tableState;
    }, [tableState, onTableStateChange, debouncedStateChange]);
}
